import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Sprout } from "lucide-react";
import api from "@/lib/api";
import { SafetyNotice } from "@/components/SafetyNotice";
import usePageTitle from "@/hooks/usePageTitle";

export default function HerbDetail() {
  const { slug } = useParams();
  const { data: h, isLoading, isError } = useQuery({
    queryKey: ["herb", slug],
    queryFn: () => api.get(`/herbs/${slug}`).then((r) => r.data),
    retry: false,
  });
  usePageTitle(h?.name);

  if (isLoading) return <p className="py-24 text-center text-brand-muted">Loading…</p>;
  if (isError || !h)
    return (
      <div className="py-24 text-center">
        <p className="text-brand-muted">Herb not found.</p>
        <Link to="/herbs" className="mt-4 inline-block text-brand underline">Back to herbs</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl px-5 sm:px-8 pt-10">
      <Link to="/herbs" data-testid="back-to-herbs" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand">
        <ArrowLeft className="h-4 w-4" /> All herbs
      </Link>

      <div className="mt-6 grid gap-8 md:grid-cols-2 md:items-start">
        <div className="overflow-hidden rounded-2xl border border-brand-line">
          <img src={h.image} alt={h.name} className="aspect-square w-full object-cover" />
        </div>
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-mist px-3 py-1 text-xs font-medium text-brand-sage">
            <Sprout className="h-3.5 w-3.5" /> {h.evidence}
          </span>
          <h1 className="mt-3 font-heading text-4xl text-brand-ink tracking-tight">{h.name}</h1>
          <p className="italic text-brand-muted">{h.botanical} · Sanskrit: {h.sanskrit}</p>
          <p className="mt-4 text-brand-ink/80 leading-relaxed">{h.summary}</p>

          <h3 className="mt-6 font-heading text-lg text-brand-ink">Properties</h3>
          <ul className="mt-2 space-y-1.5 text-sm text-brand-muted">
            {h.properties?.map((p, i) => <li key={i}>• {p}</li>)}
          </ul>
        </div>
      </div>

      <div className="mt-10 rounded-2xl border border-brand-line bg-white p-6">
        <h3 className="font-heading text-xl text-brand-ink">Traditional uses</h3>
        <ul className="mt-3 space-y-2 text-brand-ink/80">
          {h.uses?.map((u, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sage" /> {u}
            </li>
          ))}
        </ul>
      </div>

      <SafetyNotice warnings={[h.safety]} title="Safety & cautions" />

      <p className="mt-6 rounded-lg border border-brand-line bg-brand-mist p-4 text-xs text-brand-muted">
        Herb information is educational. It is not a prescription. Consult a qualified Ayurvedic practitioner or
        doctor before use, especially if pregnant, nursing, or taking medication.
      </p>
    </div>
  );
}
